import React from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button
} from "@mui/material";
import * as moment from "moment";
import cloneDeep from "lodash/cloneDeep";
import VitalRow, { vitals as vitalsModel } from "./VitalRow";
import { tl } from "../../../components/translate";
import useCurrentResourceCentre from "../../../hooks/useCurrentResourceCentre";
import { IVitals } from "../../../interfaces/AssessmentInterfaces";
import { VitalSettings } from "../../../interfaces/ResourceCentreInterface";

interface VitalsPopUpInterface {
  isOpen: boolean;
  onClose: () => void;
  aVitals?: IVitals[];
  onSave: (data: IVitals[]) => void;
}

const VitalsPopUp: React.FC<VitalsPopUpInterface> = ({ isOpen, onClose, aVitals = [], onSave }) => {
  // custom hooks and selectors
  const currentResourceCentre = useCurrentResourceCentre();
  const vitalsOrder = currentResourceCentre?.settings?.assessmentSettings?.vitalsOrder || [];

  // states
  const [vitals, setVitals] = React.useState<IVitals[]>([]);

  // memo
  const defaultVitals = React.useMemo(() => {
    const defaultVitalsArr: IVitals[] = [];
    const vitalSettings = currentResourceCentre?.settings?.vitalSettings;

    if (vitalSettings) {
      vitalsOrder.forEach((item: string) => {
        if (vitalSettings[item as keyof VitalSettings]) {
          const vitalInfo = vitalsModel.find((vt) => vt.dbKey === item);

          const formattedDefaultVital = {
            name: vitalInfo?.name || "",
            reading: vitalInfo?.defaultValue || "",
            unit: vitalInfo?.unit || "",
            extraInfo: "",
            type: vitalInfo?.type || "",
            date: moment()
          };

          if (vitalInfo) defaultVitalsArr.push(formattedDefaultVital);
        }
      });
    }
    return defaultVitalsArr;
  }, [currentResourceCentre, vitalsOrder]);

  // effects
  React.useEffect(() => {
    setVitals(aVitals?.length ? aVitals : defaultVitals);
  }, [aVitals, defaultVitals]);

  return (
    <Dialog fullWidth onClose={onClose} open={isOpen}>
      <DialogTitle
        style={{
          borderBottom: "1px solid #ececec",
          background: "#f9f9f9",
          textTransform: "uppercase"
        }}
      >
        {tl("assessment.addVital")}
      </DialogTitle>
      <DialogContent style={{ padding: "24px 32px" }}>
        <Box style={{ width: "100%", display: "flex" }}>
          <Box sx={{ width: { xs: "100%", sm: "30%" } }}>
            <Typography style={{ fontWeight: 600 }}>{tl("assessment.vital")}</Typography>
          </Box>
          <Box sx={{ display: { xs: "none", sm: "block" }, width: "30%", pl: "20px" }}>
            <Typography style={{ fontWeight: 600 }}>{tl("assessment.reading")}</Typography>
          </Box>
          <Box sx={{ display: { xs: "none", sm: "block" }, width: "40%", pl: "42px" }}>
            <Typography style={{ fontWeight: 600 }}>{tl("assessment.extraInfo")}</Typography>
          </Box>
        </Box>
        <Box style={{ width: "100%" }}>
          {vitals?.map((vi, index) => (
            <VitalRow
              key={vi.name}
              vital={vi}
              onChange={(newObj: IVitals) => {
                const newArr = vitals;
                newArr[index] = newObj;
                setVitals(newArr);
              }}
              onRemove={() => {
                const newArr = cloneDeep(vitals);
                newArr.splice(index, 1);
                setVitals(newArr);
              }}
            />
          ))}
        </Box>
        <Box
          data-testmation="vitals.AddNewButton"
          onClick={() => {
            setVitals([
              ...vitals,
              {
                name: "",
                reading: "",
                unit: "",
                extraInfo: "",
                date: moment()
              }
            ]);
          }}
          style={{ cursor: "pointer", width: "80px" }}
        >
          <Typography>
            <u>{tl("assessment.addNew")}</u>
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions
        style={{
          background: "#00800014",
          borderTop: "1px solid #00800017",
          justifyContent: "flex-start"
        }}
      >
        <Box
          width="100%"
          display="flex"
          justifyContent="space-between"
          padding="0px 24px"
          alignContent="center"
        >
          <Box />
          <Box>
            <Button
              style={{ marginRight: "8px" }}
              data-testmation="vitals.cancelButton"
              color="primary"
              onClick={() => {
                onClose();
              }}
            >
              {tl("assessment.cancel")}
            </Button>
            <Button
              data-testmation="vitalsSaveButton"
              variant="contained"
              color="primary"
              onClick={async () => {
                const filteredVitals = vitals.filter((vi) => vi.name && vi.reading);
                filteredVitals.forEach((element) => {
                  // eslint-disable-next-line no-param-reassign
                  if (element?.type) delete element.type;
                });
                await onSave(filteredVitals);
                await setVitals(defaultVitals);
                await onClose();
              }}
            >
              {tl("assessment.save")}
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default VitalsPopUp;
